import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
  CardActions,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  CircularProgress,
  LinearProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CloudUpload,
  Add,
  Delete,
  Settings,
  Storage,
  Description,
  CheckCircle,
  Error,
  Info,
  Star,
  StarBorder
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';

// ≽^•⩊•^≼ KnowledgeBaseSettings - Admin only component for managing knowledge base collections
const KnowledgeBaseSettings = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState(null);
  const [errors, setErrors] = useState([]);

  // Form states
  const [newCollectionName, setNewCollectionName] = useState('');
  const [newCollectionDescription, setNewCollectionDescription] = useState('');
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);

  // Load collections on component mount
  useEffect(() => {
    loadCollections();
  }, []);

  const loadCollections = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/knowledge-base/collections', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCollections(data);
        
        // Set default collection if exists
        const defaultCollection = data.find(col => col.is_default);
        if (defaultCollection) {
          setSelectedCollection(defaultCollection.name);
        }
      } else {
        setErrors(['Errore nel caricamento delle collezioni']);
      }
    } catch (error) {
      setErrors([`Errore di connessione: ${error.message}`]);
    } finally {
      setLoading(false);
    }
  };

  const createCollection = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/knowledge-base/collections', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newCollectionName,
          description: newCollectionDescription,
          set_as_default: setAsDefault
        })
      });

      if (response.ok) {
        setCreateDialogOpen(false);
        setNewCollectionName('');
        setNewCollectionDescription('');
        setSetAsDefault(false);
        await loadCollections();
      } else {
        const error = await response.json();
        setErrors([error.detail || 'Errore nella creazione della collezione']);
      }
    } catch (error) {
      setErrors([`Errore di connessione: ${error.message}`]);
    }
  };

  const deleteCollection = async (collectionName) => {
    if (!window.confirm(`Sei sicuro di voler eliminare la collezione "${collectionName}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/knowledge-base/collections/${collectionName}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await loadCollections();
      } else {
        const error = await response.json();
        setErrors([error.detail || 'Errore nell\'eliminazione della collezione']);
      }
    } catch (error) {
      setErrors([`Errore di connessione: ${error.message}`]);
    }
  };

  const setDefaultCollection = async (collectionName) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/knowledge-base/collections/${collectionName}/set-default`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await loadCollections();
      } else {
        const error = await response.json();
        setErrors([error.detail || 'Errore nell\'impostazione della collezione predefinita']);
      }
    } catch (error) {
      setErrors([`Errore di connessione: ${error.message}`]);
    }
  };

  const uploadFiles = async () => {
    if (!selectedCollection || selectedFiles.length === 0) {
      setErrors(['Seleziona una collezione e almeno un file']);
      return;
    }

    try {
      setUploadLoading(true);
      setUploadProgress(0);

      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });

      const token = localStorage.getItem('token');
      const response = await fetch(`/knowledge-base/collections/${selectedCollection}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();

      if (response.ok) {
        setUploadResults(result);
        setSelectedFiles([]);
        await loadCollections();
      } else {
        setErrors([result.detail || 'Errore nel caricamento dei file']);
      }
    } catch (error) {
      setErrors([`Errore di connessione: ${error.message}`]);
    } finally {
      setUploadLoading(false);
      setUploadProgress(100);
    }
  };

  // Dropzone configuration
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'application/zip': ['.zip'],
      'text/plain': ['.txt']
    },
    maxSize: 100 * 1024 * 1024, // 100MB
    onDrop: (acceptedFiles) => {
      setSelectedFiles(prev => [...prev, ...acceptedFiles]);
    }
  });

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('it-IT');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Gestione Knowledge Base
      </Typography>

      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrors([])}>
          {errors.map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </Alert>
      )}

      {/* Collections Management */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Collezioni ({collections.length})
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Nuova Collezione
          </Button>
        </Box>

        <Grid container spacing={2}>
          {collections.map((collection) => (
            <Grid item xs={12} md={6} lg={4} key={collection.id}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Typography variant="h6" component="div">
                      {collection.name}
                    </Typography>
                    <Box>
                      {collection.is_default && (
                        <Chip 
                          icon={<Star />} 
                          label="Predefinita" 
                          color="primary" 
                          size="small" 
                        />
                      )}
                    </Box>
                  </Box>
                  
                  {collection.description && (
                    <Typography color="text.secondary" gutterBottom>
                      {collection.description}
                    </Typography>
                  )}

                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>Documenti:</strong> {collection.total_documents}
                    </Typography>
                    <Typography variant="body2">
                      <strong>File processati:</strong> {collection.total_files_processed}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Modello:</strong> {collection.embedding_model}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Creata:</strong> {formatDate(collection.created_at)}
                    </Typography>
                  </Box>
                </CardContent>
                
                <CardActions>
                  {!collection.is_default && (
                    <Tooltip title="Imposta come predefinita">
                      <IconButton 
                        size="small"
                        onClick={() => setDefaultCollection(collection.name)}
                      >
                        <StarBorder />
                      </IconButton>
                    </Tooltip>
                  )}
                  
                  <Button 
                    size="small" 
                    onClick={() => {
                      setSelectedCollection(collection.name);
                      setUploadDialogOpen(true);
                    }}
                  >
                    Carica File
                  </Button>
                  
                  <IconButton 
                    size="small" 
                    color="error"
                    onClick={() => deleteCollection(collection.name)}
                  >
                    <Delete />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {collections.length === 0 && (
          <Alert severity="info">
            Nessuna collezione creata. Crea la tua prima collezione per iniziare a caricare documenti.
          </Alert>
        )}
      </Paper>

      {/* Create Collection Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Crea Nuova Collezione</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome Collezione"
            fullWidth
            variant="outlined"
            value={newCollectionName}
            onChange={(e) => setNewCollectionName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Descrizione (opzionale)"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={newCollectionDescription}
            onChange={(e) => setNewCollectionDescription(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={setAsDefault}
                onChange={(e) => setSetAsDefault(e.target.checked)}
              />
            }
            label="Imposta come collezione predefinita"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Annulla</Button>
          <Button 
            onClick={createCollection} 
            variant="contained"
            disabled={!newCollectionName.trim()}
          >
            Crea
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Files Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Carica File nella Collezione: {selectedCollection}
        </DialogTitle>
        <DialogContent>
          {/* File Drop Zone */}
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed #ccc',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: isDragActive ? 'action.hover' : 'background.paper',
              mb: 2
            }}
          >
            <input {...getInputProps()} />
            <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" gutterBottom>
              {isDragActive ? 'Rilascia i file qui...' : 'Trascina i file qui o clicca per selezionare'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Formati supportati: PDF, ZIP, TXT (max 100MB per file)
            </Typography>
          </Box>

          {/* Selected Files List */}
          {selectedFiles.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                File Selezionati ({selectedFiles.length})
              </Typography>
              <List>
                {selectedFiles.map((file, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <Description />
                    </ListItemIcon>
                    <ListItemText
                      primary={file.name}
                      secondary={formatFileSize(file.size)}
                    />
                    <IconButton onClick={() => removeFile(index)}>
                      <Delete />
                    </IconButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Upload Progress */}
          {uploadLoading && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                Caricamento in corso...
              </Typography>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}

          {/* Upload Results */}
          {uploadResults && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Risultati Caricamento
              </Typography>
              
              {uploadResults.processed_files.length > 0 && (
                <Alert severity="success" sx={{ mb: 1 }}>
                  {uploadResults.processed_files.length} file processati con successo.
                  Totale chunks creati: {uploadResults.total_chunks}
                </Alert>
              )}
              
              {uploadResults.errors.length > 0 && (
                <Alert severity="error">
                  Errori durante il processamento:
                  <ul>
                    {uploadResults.errors.map((error, index) => (
                      <li key={index}>{error.filename}: {error.error}</li>
                    ))}
                  </ul>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setUploadDialogOpen(false);
            setSelectedFiles([]);
            setUploadResults(null);
          }}>
            Chiudi
          </Button>
          <Button 
            onClick={uploadFiles} 
            variant="contained"
            disabled={selectedFiles.length === 0 || uploadLoading}
            startIcon={uploadLoading ? <CircularProgress size={20} /> : <CloudUpload />}
          >
            {uploadLoading ? 'Caricamento...' : 'Carica File'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KnowledgeBaseSettings;
